import * as React from "react";

import { getParentElement } from "@impero-com/common/utils/dom";
import { assertNever } from "@impero-com/common/utils/obj";
import { nullToEmpty } from "@impero-com/common/utils/string";
import { Select } from "antd";
import type { RefSelectProps } from "antd/es/select";

import {
    CustomizedValidation,
    EditFormCreate,
    FormComponentProps,
    ValidationRule,
    validationRulesConverter,
} from "utils/form";

import { BaseFormDisplay } from "./formDisplay";
import * as theme from "./theme.css";

import { BaseFormField, EditInput, EditProps } from ".";
const { Option } = Select;

const FORM_SELECT_UNDEFINED_VALUE = "UNDEFINED_VALUE";

/**
 * The definition of an Option on a Select element
 * @field value the "real", "inner" value of the Select field once selected (to be sent in a JSON probably)
 * @field label the label that the Select will show for the user to select
 */
interface SelectInputOption {
    value: string | undefined;
    label: string;
}

interface RawCreateSelectInputProps<
    ID extends string,
    R,
> extends FormComponentProps<ID, string | undefined, R> {
    id: ID;
    initialValue: string | undefined;
    validators?: ValidationRule<string | undefined>[];
    options: SelectInputOption[];
    className?: string;
    disabled?: boolean;
    // Should be (in) the scrollable container
    getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
    placeholder?: string;
    value?: string;
    onChange?: (value: string | undefined) => void;
    dropdownMatchSelectWidth?: boolean;
    showSearch?: boolean;
    validationError?: CustomizedValidation;
    children?: React.ReactNode;
}

class RawCreateSelectInput<ID extends string, R> extends React.PureComponent<
    RawCreateSelectInputProps<ID, R>
> {
    private innerField = React.createRef<RefSelectProps>();

    render = () => {
        const {
            options,
            className,
            disabled,
            getPopupContainer = getParentElement,
            placeholder,
            value,
            onChange,
            dropdownMatchSelectWidth,
            showSearch,
        } = this.props;
        return (
            <Select
                className={`${theme.imperoSelect} ${nullToEmpty(className)}`}
                placeholder={placeholder}
                ref={this.innerField}
                getPopupContainer={getPopupContainer}
                disabled={disabled}
                value={value}
                dropdownMatchSelectWidth={dropdownMatchSelectWidth}
                onChange={onChange}
                showSearch={showSearch}
            >
                {options.map((opt) => (
                    <Option
                        key={
                            opt.value !== undefined
                                ? opt.value
                                : FORM_SELECT_UNDEFINED_VALUE
                        }
                        value={opt.value}
                    >
                        {opt.label}
                    </Option>
                ))}
            </Select>
        );
    };

    focus = () => {
        const field = this.innerField.current;
        if (field) {
            field.focus();
        }
    };
}

interface CreateSelectInputProps<
    ID extends string,
    R,
> extends RawCreateSelectInputProps<ID, R> {
    label?: string;
    children?: React.ReactNode;
}

class CreateSelectInput<ID extends string, R> extends React.PureComponent<
    CreateSelectInputProps<ID, R>
> {
    private rawInput = React.createRef<RawCreateSelectInput<ID, R>>();

    render = () => {
        const { label, validationError } = this.props;

        return (
            <BaseFormField
                getComponent={() => this.getComponent(this.props.options)}
                onClick={this.onLabelClick}
                hasFeedback={false}
                validationError={validationError}
                label={label}
            />
        );
    };

    getComponent = (options: SelectInputOption[]) => {
        const {
            form,
            id,
            validators,
            label,
            className,
            initialValue,
            disabled,
            getPopupContainer,
            placeholder,
            dropdownMatchSelectWidth,
            showSearch,
        } = this.props;
        const { getFieldDecorator } = form;
        return getFieldDecorator(id, {
            rules: validationRulesConverter<string | undefined>()(
                id,
                form,
                validators,
                label
            ),
            valuePropName: "value",
            trigger: "onChange",
            validateFirst: true,
            preserve: true,
            initialValue,
        })(
            <RawCreateSelectInput
                form={form}
                id={id}
                options={options}
                validators={validators}
                className={className}
                initialValue={initialValue}
                disabled={disabled}
                getPopupContainer={getPopupContainer}
                placeholder={placeholder}
                ref={this.rawInput}
                dropdownMatchSelectWidth={dropdownMatchSelectWidth}
                showSearch={showSearch}
            />
        );
    };

    onLabelClick = () => {
        if (this.rawInput.current) {
            this.rawInput.current.focus();
        }
    };
}

interface EditSelectInputProps extends EditProps<string | undefined> {
    // Should be (in) the scrollable container
    getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
    placeholder?: string;
    options: SelectInputOption[];
    label?: string;
    className?: string;
    disabled?: boolean;
    dropdownMatchSelectWidth?: boolean;
    showSearch?: boolean;
    children?: React.ReactNode;
}

const EditSelectInputForm = EditFormCreate<
    string | undefined,
    EditSelectInputProps
>()(CreateSelectInput);
const EditSelectInput = EditInput<string | undefined, EditSelectInputProps>(
    EditSelectInputForm
);

const RawEditSelectInputForm = EditFormCreate<
    string | undefined,
    EditSelectInputProps
>()(RawCreateSelectInput);
const RawEditSelectInput = EditInput<string | undefined, EditSelectInputProps>(
    RawEditSelectInputForm
);

interface RawSelectInputCommonProps {
    value: string | undefined;
    options: SelectInputOption[];
    className?: string;
    placeholder?: string;
    children?: React.ReactNode;
}

interface SelectInputCommonProps extends RawSelectInputCommonProps {
    label: string;
    children?: React.ReactNode;
}

class RawReadOnlySelectInput extends React.PureComponent<RawSelectInputCommonProps> {
    render = () => {
        const { value, className, placeholder } = this.props;
        return (
            <Select
                disabled
                className={`${theme.imperoSelect} ${nullToEmpty(className)}`}
                value={value}
                placeholder={placeholder}
            >
                {this.props.options.map((opt) => (
                    <Option
                        key={
                            opt.value !== undefined
                                ? opt.value
                                : FORM_SELECT_UNDEFINED_VALUE
                        }
                        value={opt.value}
                    >
                        {opt.label}
                    </Option>
                ))}
            </Select>
        );
    };
}

class ReadOnlySelectInput extends React.PureComponent<SelectInputCommonProps> {
    render = () => {
        const { label, value, className, placeholder, options } = this.props;
        return (
            <BaseFormDisplay label={label}>
                <RawReadOnlySelectInput
                    options={options}
                    value={value}
                    className={className}
                    placeholder={placeholder}
                />
            </BaseFormDisplay>
        );
    };
}

type SelectInputProps<ID extends string, R> =
    | ({ type: "READ_ONLY" } & SelectInputCommonProps)
    | ({ type: "EDIT" } & EditSelectInputProps & SelectInputCommonProps)
    | ({ type: "CREATE" } & CreateSelectInputProps<ID, R>);

class SelectInput<ID extends string, R> extends React.PureComponent<
    SelectInputProps<ID, R>
> {
    render = () => {
        switch (this.props.type) {
            case "READ_ONLY": {
                const {
                    label,
                    value,
                    options,
                    className,
                    placeholder,
                    children,
                    type: _type,
                } = this.props;
                return (
                    <ReadOnlySelectInput
                        label={label}
                        value={value}
                        options={options}
                        className={className}
                        placeholder={placeholder}
                        children={children}
                    />
                );
            }
            case "EDIT": {
                const {
                    getPopupContainer,
                    placeholder,
                    id,
                    validators,
                    value,
                    onChange,
                    children,
                    options,
                    label,
                    className,
                    showSearch,
                    validationError,
                    type: _type,
                } = this.props;
                return (
                    <EditSelectInput
                        getPopupContainer={getPopupContainer}
                        placeholder={placeholder}
                        label={label}
                        id={id}
                        validators={validators}
                        value={value}
                        onChange={onChange}
                        options={options}
                        children={children}
                        className={className}
                        showSearch={showSearch}
                        validationError={validationError}
                    />
                );
            }
            case "CREATE": {
                const {
                    label,
                    id,
                    initialValue,
                    validators,
                    options,
                    className,
                    disabled,
                    getPopupContainer,
                    placeholder,
                    form,
                    wrappedComponentRef,
                    children,
                    dropdownMatchSelectWidth,
                    showSearch,
                    type: _type,
                } = this.props;
                return (
                    <CreateSelectInput
                        label={label}
                        id={id}
                        initialValue={initialValue}
                        validators={validators}
                        options={options}
                        className={className}
                        disabled={disabled}
                        getPopupContainer={getPopupContainer}
                        placeholder={placeholder}
                        form={form}
                        wrappedComponentRef={wrappedComponentRef}
                        children={children}
                        dropdownMatchSelectWidth={dropdownMatchSelectWidth}
                        showSearch={showSearch}
                    />
                );
            }
            default:
                return assertNever(this.props);
        }
    };
}

type RawSelectInputProps<ID extends string, R> =
    | ({ type: "READ_ONLY" } & RawSelectInputCommonProps)
    | ({ type: "EDIT" } & EditSelectInputProps & RawSelectInputCommonProps)
    | ({ type: "CREATE" } & RawCreateSelectInputProps<ID, R>);

class RawSelectInput<ID extends string, R> extends React.PureComponent<
    RawSelectInputProps<ID, R>
> {
    render = () => {
        const props = this.props;
        switch (props.type) {
            case "READ_ONLY": {
                const {
                    value,
                    options,
                    className,
                    placeholder,
                    children,
                    type: _type,
                } = props;
                return (
                    <RawReadOnlySelectInput
                        value={value}
                        options={options}
                        className={className}
                        placeholder={placeholder}
                        children={children}
                    />
                );
            }
            case "EDIT": {
                const {
                    getPopupContainer,
                    placeholder,
                    id,
                    validators,
                    value,
                    onChange,
                    children,
                    options,
                    className,
                    dropdownMatchSelectWidth,
                    showSearch,
                    type: _type,
                } = props;
                return (
                    <RawEditSelectInput
                        getPopupContainer={getPopupContainer}
                        options={options}
                        placeholder={placeholder}
                        id={id}
                        validators={validators}
                        value={value}
                        onChange={onChange}
                        children={children}
                        className={className}
                        dropdownMatchSelectWidth={dropdownMatchSelectWidth}
                        showSearch={showSearch}
                    />
                );
            }
            case "CREATE": {
                const {
                    id,
                    initialValue,
                    validators,
                    options,
                    className,
                    disabled,
                    getPopupContainer,
                    placeholder,
                    form,
                    wrappedComponentRef,
                    children,
                    dropdownMatchSelectWidth,
                    showSearch,
                    type: _type,
                } = props;
                return (
                    <RawCreateSelectInput
                        id={id}
                        initialValue={initialValue}
                        validators={validators}
                        options={options}
                        className={className}
                        disabled={disabled}
                        getPopupContainer={getPopupContainer}
                        placeholder={placeholder}
                        form={form}
                        wrappedComponentRef={wrappedComponentRef}
                        children={children}
                        dropdownMatchSelectWidth={dropdownMatchSelectWidth}
                        showSearch={showSearch}
                    />
                );
            }
            default:
                return assertNever(props);
        }
    };
}

export type {
    SelectInputProps,
    SelectInputOption,
    SelectInputCommonProps,
    EditSelectInputProps,
    CreateSelectInputProps,
};
export { SelectInput, RawSelectInput, FORM_SELECT_UNDEFINED_VALUE };
